import React, { useContext, useEffect, useState } from 'react';

import get from 'lodash/get';
import { AppType } from '../../../store/store';
import css from './SetPlan.module.scss';
import PlansContainer from './PlansContainers/PlansContainer';
import PartnerMaxOnlyPlansContainer from './PlansContainers/PartnerMaxOnlyPlansContainer';
import PlanDetails from './PlanDetails';
import { AnalyticsContext } from '../../../ModularReduxAnalytics';
import LocationSharingLearnMore from './LocationSharingLearnMore';
import PlanFlowMaxPlansContainer from './PlansContainers/PlanFlowMaxPlansContainer';
import ChoosePlanContainer from './PlansContainers/ChoosePlanContainer';
import DefaultMaxPlanContainer from './PlansContainers/DefaultMaxPlanContainer';
import DefaultInfinityPlanContainer from './PlansContainers/DefaultInfinityPlanContainer';
import { useToggles } from '../../../state/toggles';
import { planPrices, savingsRewards } from '../../../utils/prices';

export const WIDGET_NAME = 'set_plan';

export enum SetPlanScreen {
  PlansListScreen,
  PlanDetailsScreen,
  LocationSharingLearnMoreScreen,
  PartnerPlansListScreen,
  PlanFlowMaxScreen,
  ChoosePlanScreen,
  DefaultMax,
  DefaultInfinity
}

export type LearnMoreTopic = SetPlanScreen.LocationSharingLearnMoreScreen;

interface Props {
  app: AppType;
  user: any;
  featureToggles: any;
  answer: any;
  widget: string;
}

export enum Plan {
  standard = 'standard',
  infinity = 'infinity',
  max = 'max'
}

const SetPlan = ({ app, user, featureToggles, answer }: Props) => {
  window.scrollTo(0, 0);
  const {
    defaultInfinity,
    defaultMax,
    planFlow,
    enableGSRChange,
    enableCorePriceChange,
    riskFreePaintedDoor
  } = useToggles();

  const { analytics } = useContext(AnalyticsContext);

  const plans = planPrices({ enableCorePriceChange, enableGSRChange });
  const savings = savingsRewards({ enableGSRChange });
  const isEnableCorePriceChangeEnabled = enableCorePriceChange.enabled;

  const getDefaultScreen = (): SetPlanScreen => {
    if (defaultInfinity.enabled) return SetPlanScreen.DefaultInfinity;
    if (defaultMax.enabled) return SetPlanScreen.DefaultMax;
    const isWorldStridesPartner = user.partner === 'WS';
    const isMaxPlanFlow =
      planFlow.enabled && planFlow.variables?.planName === 'max';
    if (isMaxPlanFlow) return SetPlanScreen.PlanFlowMaxScreen;
    if (isWorldStridesPartner) return SetPlanScreen.PartnerPlansListScreen;
    return SetPlanScreen.PlansListScreen;
  };

  const [screens, setScreens] = useState<SetPlanScreen[]>([getDefaultScreen()]);
  const currentScreen = screens[screens.length - 1];

  const goToScreen = (targetScreen: SetPlanScreen) => {
    setScreens([...screens, targetScreen]);
  };
  const goBack = () => {
    if (screens.length > 1) {
      setScreens(screens.slice(0, -1));
    }
  };

  const [plan, setPlan] = useState<Plan>(Plan.standard);

  const defaultMaxFTDEventProps = {
    feature_key: 'acquisition_defaultMax',
    variation_key: defaultMax.variationKey,
    toggle_enabled: defaultMax.enabled,
    experiment_key: 'acquisition_defaultMax_test'
  };

  const defaultInfinityFTDEventProps = {
    feature_key: 'acquisition_defaultInfinity',
    variation_key: defaultInfinity.variationKey,
    toggle_enabled: defaultInfinity.enabled,
    experiment_key: 'acquisition_defaultInfinity_test'
  };

  useEffect(() => {
    let name = '';
    analytics.trackEvent('Feature Toggle Decided', {
      eventProps: defaultInfinityFTDEventProps
    });
    analytics.trackEvent('Feature Toggle Decided', {
      eventProps: defaultMaxFTDEventProps
    });
    if (defaultMax.enabled) {
      name = 'Default Max';
    } else if (defaultInfinity.enabled) {
      name = 'Default Infinity';
    }

    analytics.trackEvent('Registration Invest Pricing Page Viewed', {
      eventProps: {
        name
      }
    });
  }, []);

  useEffect(() => {
    if (user.partner === 'WS') {
      setScreens([SetPlanScreen.PartnerPlansListScreen]);
    }
  }, [user.partner]);

  const submitPlanChange = (pricingPlanName?: string) => {
    if (!pricingPlanName) {
      answer({
        accessToken: app.registrationToken,
        questions: [WIDGET_NAME],
        answers: [
          {
            pricingPlanName: 'standard',
            ...(isEnableCorePriceChangeEnabled
              ? { basePrice: plans.standard.toString() }
              : {}),
            riskFreeEnabled: riskFreePaintedDoor.enabled
          }
        ]
      });
    } else {
      answer({
        accessToken: app.registrationToken,
        questions: [WIDGET_NAME],
        answers: [
          {
            pricingPlanName,
            ...(isEnableCorePriceChangeEnabled
              ? {
                  basePrice:
                    plans[pricingPlanName]?.toString() ??
                    plans.standard.toString()
                }
              : {}),
            riskFreeEnabled: riskFreePaintedDoor.enabled
          }
        ]
      });
    }
  };

  return (
    <div data-e2e="upgrade-plans" className={css.invest}>
      {currentScreen === SetPlanScreen.PlanFlowMaxScreen && (
        <PlanFlowMaxPlansContainer
          app={app}
          plans={plans}
          onUpgradeClicked={submitPlanChange}
          seeAllPlans={() => goToScreen(SetPlanScreen.ChoosePlanScreen)}
          onInfoClicked={(selectedPlan: Plan) => {
            setPlan(selectedPlan);
            goToScreen(SetPlanScreen.PlanDetailsScreen);
          }}
        />
      )}
      {currentScreen === SetPlanScreen.PartnerPlansListScreen && (
        <PartnerMaxOnlyPlansContainer
          plans={plans}
          app={app}
          savings={savings}
          onUpgradeClicked={submitPlanChange}
          analytics={analytics}
        />
      )}
      {currentScreen === SetPlanScreen.PlansListScreen && (
        <PlansContainer
          featureToggles={featureToggles}
          savings={savings}
          plans={plans}
          app={app}
          user={user}
          onSkip={submitPlanChange}
          onBackClicked={goBack}
          onInfoClicked={(selectedPlan: Plan) => {
            setPlan(selectedPlan);
            goToScreen(SetPlanScreen.PlanDetailsScreen);
          }}
          onLearnMoreClicked={(
            learnMoreTopic: LearnMoreTopic,
            selectedPlan: Plan
          ) => {
            setPlan(selectedPlan);
            goToScreen(learnMoreTopic);
          }}
          onUpgradeClicked={submitPlanChange}
        />
      )}
      {currentScreen === SetPlanScreen.PlanDetailsScreen && (
        <PlanDetails
          price={plans[plan]}
          plan={plan}
          loading={!!app.loading}
          flowId={app.flowId ?? ''}
          onBackClicked={goBack}
          onSkip={submitPlanChange}
          onUpgradeClicked={submitPlanChange}
          analytics={analytics}
        />
      )}
      {currentScreen === SetPlanScreen.LocationSharingLearnMoreScreen && (
        <LocationSharingLearnMore
          onBackClicked={goBack}
          onUpgradeClicked={() => {
            analytics.trackEvent('Registration Invest Plan Action', {
              eventProps: {
                plan,
                price: plans[plan],
                skipped: false
              }
            });
            submitPlanChange(plan);
          }}
          plan={plan}
          loading={!!app.loading}
          analytics={analytics}
        />
      )}
      {currentScreen === SetPlanScreen.ChoosePlanScreen && (
        <ChoosePlanContainer
          plans={plans}
          app={app}
          onUpgradeClicked={submitPlanChange}
          onBackClicked={goBack}
          onInfoClicked={(selectedPlan: Plan) => {
            setPlan(selectedPlan);
            goToScreen(SetPlanScreen.PlanDetailsScreen);
          }}
          onSkip={submitPlanChange}
        />
      )}
      {currentScreen === SetPlanScreen.DefaultMax && (
        <DefaultMaxPlanContainer
          plans={plans}
          savings={savings}
          app={app}
          onUpgradeClicked={submitPlanChange}
          onBackClicked={goBack}
          onInfoClicked={(selectedPlan: Plan) => {
            setPlan(selectedPlan);
            goToScreen(SetPlanScreen.PlanDetailsScreen);
          }}
          onExploreAllPlansClicked={() => {
            goToScreen(SetPlanScreen.PlansListScreen);
          }}
          onSkip={submitPlanChange}
        />
      )}
      {currentScreen === SetPlanScreen.DefaultInfinity && (
        <DefaultInfinityPlanContainer
          plans={plans}
          savings={savings}
          app={app}
          onUpgradeClicked={submitPlanChange}
          onBackClicked={goBack}
          onInfoClicked={(selectedPlan: Plan) => {
            setPlan(selectedPlan);
            goToScreen(SetPlanScreen.PlanDetailsScreen);
          }}
          onExploreAllPlansClicked={() => {
            goToScreen(SetPlanScreen.PlansListScreen);
          }}
          onSkip={submitPlanChange}
        />
      )}
    </div>
  );
};

export default SetPlan;
